import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiUrl } from '../config/api';

const fmtMoney = (value) => Number(value || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (value) => Number(value || 0).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
const fmtDateTime = (value) => value ? new Date(value).toLocaleString('ru-RU') : '—';
const fmtDate = (value) => value ? new Date(`${value}T00:00:00`).toLocaleDateString('ru-RU') : '—';
const todayIso = () => new Date().toISOString().slice(0, 10);

function requestStatusLabel(status) {
  const map = {
    draft: 'Черновик',
    in_progress: 'В обработке',
    awaiting_gd: 'На утверждении у ГД',
    done: 'Выполнено',
  };
  return map[String(status || '').trim()] || '—';
}

function sourceLabel(item) {
  if (item.sourceType === 'manual') return 'Ручное добавление';
  const parts = [];
  if (item.sourceRequestId) parts.push(`Заявка №${item.sourceRequestId}`);
  if (item.requestMonth && item.requestYear) parts.push(`${String(item.requestMonth).padStart(2, '0')}.${item.requestYear}`);
  const status = requestStatusLabel(item.requestStatus);
  if (status !== '—') parts.push(status);
  return parts.join(' • ') || 'Заявка';
}

function movedFromLabel(item) {
  if (!item?.movedFromObjectId) return '';
  const name = String(item.movedFromObjectName || '').trim();
  const code = item.movedFromObjectCode == null ? '' : String(item.movedFromObjectCode);
  if (code && name) return `${code} • ${name}`;
  return name || code || `ID ${item.movedFromObjectId}`;
}

export default function WarehouseObjectPage() {
  const { id } = useParams();
  const [payload, setPayload] = useState(null);
  const [tmcOptions, setTmcOptions] = useState([]);
  const [calcOptions, setCalcOptions] = useState([]);
  const [tmcLoading, setTmcLoading] = useState(false);
  const [calcLoading, setCalcLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [calcImportOpen, setCalcImportOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [objectsLoading, setObjectsLoading] = useState(false);
  const [objects, setObjects] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set());
  const [positionFilters, setPositionFilters] = useState({
    name: '',
    unit: '',
    amortization: '',
  });
  const [moveForm, setMoveForm] = useState({
    targetObjectId: '',
    moveDate: todayIso(),
    query: '',
  });
  const [batchWriteOffOpen, setBatchWriteOffOpen] = useState(false);
  const [batchWriteOffDate, setBatchWriteOffDate] = useState(todayIso());
  const [batchWriteOffComment, setBatchWriteOffComment] = useState('');
  const [writeOffTarget, setWriteOffTarget] = useState(null);
  const [writeOffDate, setWriteOffDate] = useState(todayIso());
  const [writeOffComment, setWriteOffComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [manualForm, setManualForm] = useState({
    tmcId: '',
    quantity: '1',
    comment: '',
    query: '',
  });
  const [calcImportForm, setCalcImportForm] = useState({
    objectCode: '',
  });

  const load = React.useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    fetch(apiUrl(`/api/warehouse/${encodeURIComponent(id)}`))
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data?.error || 'load_failed');
        return data;
      })
      .then((data) => {
        setPayload(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Ошибка загрузки склада объекта');
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const objectName = payload?.object?.name || 'Склад объекта';
    document.title = `Склад — ${objectName}`;
  }, [payload]);

  useEffect(() => {
    if (!manualOpen || tmcOptions.length) return;
    setTmcLoading(true);
    fetch(apiUrl('/api/tmc'))
      .then(async (r) => {
        const data = await r.json().catch(() => []);
        if (!r.ok) throw new Error('tmc_load_failed');
        return Array.isArray(data) ? data : [];
      })
      .then((rows) => {
        const filtered = rows
          .filter((item) => Number(item?.amortization_period) > 0 && String(item?.group_id ?? '') !== '1')
          .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'ru'));
        setTmcOptions(filtered);
      })
      .catch(() => {
        alert('Не удалось загрузить справочник ТМЦ');
      })
      .finally(() => {
        setTmcLoading(false);
      });
  }, [manualOpen, tmcOptions.length]);

  useEffect(() => {
    if (!calcImportOpen || !id) return;
    setCalcLoading(true);
    fetch(apiUrl(`/api/warehouse/${encodeURIComponent(id)}/card-calcs`))
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data?.error || 'calc_load_failed');
        return Array.isArray(data?.items) ? data.items : [];
      })
      .then((rows) => {
        setCalcOptions(rows);
        setCalcImportForm((prev) => ({
          objectCode: prev.objectCode || String(rows[0]?.objectCode || ''),
        }));
      })
      .catch(() => {
        setCalcOptions([]);
        alert('Не удалось загрузить расчеты объекта');
      })
      .finally(() => {
        setCalcLoading(false);
      });
  }, [calcImportOpen, id]);

  useEffect(() => {
    if (!moveModalOpen || objects.length) return;
    setObjectsLoading(true);
    fetch(apiUrl('/api/objects'))
      .then(async (r) => {
        const data = await r.json().catch(() => ([]));
        if (!r.ok) throw new Error('objects_load_failed');
        return Array.isArray(data) ? data : [];
      })
      .then((rows) => {
        const sorted = rows
          .map((row) => ({
            id: Number(row.id) || 0,
            codeBase: Number(row.codeBase) || 0,
            name: String(row.name || ''),
            address: String(row.address || ''),
            customer: String(row.customer || ''),
          }))
          .filter((row) => row.id > 0)
          .sort((a, b) => Number(a.codeBase) - Number(b.codeBase));
        setObjects(sorted);
      })
      .catch(() => {
        alert('Не удалось загрузить список объектов');
      })
      .finally(() => {
        setObjectsLoading(false);
      });
  }, [moveModalOpen, objects.length]);

  const items = useMemo(() => (Array.isArray(payload?.items) ? payload.items : []), [payload?.items]);
  const activeItems = useMemo(() => items.filter((item) => !item.writeOffDate), [items]);
  const writtenOffItems = useMemo(() => items.filter((item) => item.writeOffDate), [items]);
  const groupedActiveItems = useMemo(() => {
    const groups = new Map();
    activeItems.forEach((item) => {
      const key = item.tmcId ? `tmc:${item.tmcId}` : `fallback:${item.id}`;
      const current = groups.get(key) || {
        key,
        tmcId: item.tmcId || null,
        name: item.name,
        unit: item.unit,
        amortizationPeriod: item.amortizationPeriod,
        quantity: 0,
        totalAmount: 0,
        details: [],
      };
      current.quantity += Number(item.quantity) || 0;
      current.totalAmount += (Number(item.quantity) || 0) * (Number(item.price) || 0);
      current.details.push(item);
      groups.set(key, current);
    });
    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        avgPrice: group.quantity > 0 ? group.totalAmount / group.quantity : 0,
        details: group.details.slice().sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()),
      }))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ru'));
  }, [activeItems]);
  const filteredGroupedActiveItems = useMemo(() => {
    const nameNeedle = String(positionFilters.name || '').trim().toLowerCase();
    const unitNeedle = String(positionFilters.unit || '').trim().toLowerCase();
    const amortNeedle = String(positionFilters.amortization || '').trim().toLowerCase();
    return groupedActiveItems.filter((group) => {
      const nameOk = !nameNeedle || String(group.name || '').toLowerCase().includes(nameNeedle);
      const unitOk = !unitNeedle || String(group.unit || '').toLowerCase().includes(unitNeedle);
      const amortText = group.amortizationPeriod == null ? '' : String(group.amortizationPeriod);
      const amortOk = !amortNeedle || amortText.toLowerCase().includes(amortNeedle);
      return nameOk && unitOk && amortOk;
    });
  }, [groupedActiveItems, positionFilters]);
  const object = payload?.object || null;
  const summary = payload?.summary || { totalPositions: 0, activePositions: 0, writtenOffPositions: 0, manualPositions: 0, activeQuantity: 0, activeAmount: 0 };
  const selectedTmc = useMemo(() => tmcOptions.find((item) => String(item.id) === String(manualForm.tmcId || '')) || null, [tmcOptions, manualForm.tmcId]);
  const filteredTmcOptions = useMemo(() => {
    const needle = String(manualForm.query || '').trim().toLowerCase();
    if (!needle) return tmcOptions;
    return tmcOptions.filter((item) => {
      const haystack = `${item.id || ''} ${item.name || ''} ${item.unit || ''} ${item.supplier_link || ''}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [tmcOptions, manualForm.query]);
  const selectedCalc = useMemo(() => calcOptions.find((item) => String(item.objectCode) === String(calcImportForm.objectCode || '')) || null, [calcOptions, calcImportForm.objectCode]);
  const activeItemIds = useMemo(() => new Set(activeItems.map((item) => Number(item.id))), [activeItems]);
  const targetObjects = useMemo(() => {
    const currentId = Number(object?.id) || 0;
    const query = String(moveForm.query || '').trim().toLowerCase();
    return objects
      .filter((row) => Number(row.id) !== currentId)
      .filter((row) => {
        if (!query) return true;
        const haystack = `${row.codeBase} ${row.name} ${row.address} ${row.customer}`.toLowerCase();
        return haystack.includes(query);
      });
  }, [objects, object?.id, moveForm.query]);
  const selectedCount = selectedItemIds.size;
  const [moveQuantities, setMoveQuantities] = useState({});
  const selectedItemsForMove = useMemo(() => {
    const ids = Array.from(selectedItemIds).map((value) => Number(value)).filter((value) => value > 0);
    if (!ids.length) return [];
    const byId = new Map(activeItems.map((item) => [Number(item.id), item]));
    return ids.map((idValue) => byId.get(idValue)).filter(Boolean);
  }, [selectedItemIds, activeItems]);
  const visibleItemIds = useMemo(
    () => filteredGroupedActiveItems.flatMap((group) => group.details.map((item) => Number(item.id))).filter((value) => value > 0),
    [filteredGroupedActiveItems]
  );
  const allVisibleSelected = visibleItemIds.length > 0 && visibleItemIds.every((idValue) => selectedItemIds.has(idValue));

  const filterInputStyle = {
    width: '100%',
    padding: '6px 8px',
    fontSize: 13,
    borderRadius: 8,
    border: '1px solid #cbd5e1',
    boxSizing: 'border-box',
    minWidth: 0,
  };

  useEffect(() => {
    setSelectedItemIds((prev) => {
      const next = new Set(Array.from(prev).filter((value) => activeItemIds.has(Number(value))));
      return next;
    });
  }, [activeItemIds]);

  useEffect(() => {
    setMoveQuantities((prev) => {
      const next = {};
      selectedItemsForMove.forEach((item) => {
        const key = String(item.id);
        const available = Number(item.quantity) || 0;
        const prevQty = Number(prev[key]);
        if (Number.isFinite(prevQty) && prevQty > 0) {
          next[key] = Math.min(prevQty, available);
        } else {
          next[key] = available;
        }
      });
      return next;
    });
  }, [selectedItemsForMove]);

  useEffect(() => {
    if (!moveModalOpen) return;
    if (moveForm.targetObjectId) {
      const exists = targetObjects.some((row) => String(row.id) === String(moveForm.targetObjectId));
      if (exists) return;
    }
    const first = targetObjects[0] || null;
    if (first?.id) {
      setMoveForm((prev) => ({ ...prev, targetObjectId: String(first.id) }));
    }
  }, [moveModalOpen, moveForm.targetObjectId, targetObjects]);

  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleItemSelection = (itemId, checked) => {
    const normalizedId = Number(itemId) || 0;
    if (!normalizedId) return;
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(normalizedId);
      else next.delete(normalizedId);
      return next;
    });
  };

  const toggleGroupSelection = (group, checked) => {
    const ids = Array.isArray(group?.details) ? group.details.map((item) => Number(item.id)).filter((value) => value > 0) : [];
    if (!ids.length) return;
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      ids.forEach((value) => {
        if (checked) next.add(value);
        else next.delete(value);
      });
      return next;
    });
  };

  const openMoveModal = () => {
    if (!selectedCount) {
      alert('Отметьте галочками позиции для перемещения');
      return;
    }
    setMoveForm((prev) => ({
      ...prev,
      moveDate: prev.moveDate || todayIso(),
      query: '',
    }));
    setMoveQuantities(() => {
      const initial = {};
      selectedItemsForMove.forEach((item) => {
        initial[String(item.id)] = Number(item.quantity) || 0;
      });
      return initial;
    });
    setMoveModalOpen(true);
  };

  const submitMoveSelected = async () => {
    const moveItems = selectedItemsForMove
      .map((item) => ({
        id: Number(item.id) || 0,
        quantity: Number(moveQuantities[String(item.id)]),
        maxQuantity: Number(item.quantity) || 0,
      }))
      .filter((row) => Number.isInteger(row.id) && row.id > 0 && Number.isFinite(row.quantity) && row.quantity > 0);
    const targetObjectId = Number(moveForm.targetObjectId) || 0;
    const moveDate = String(moveForm.moveDate || '').trim();
    if (!moveItems.length) {
      alert('Нет выбранных позиций для перемещения');
      return;
    }
    const invalidRow = moveItems.find((row) => row.quantity - row.maxQuantity > 0.000001);
    if (invalidRow) {
      alert('Количество для перемещения не может быть больше доступного');
      return;
    }
    if (!targetObjectId) {
      alert('Выберите объект назначения');
      return;
    }
    if (!moveDate) {
      alert('Укажите дату перемещения');
      return;
    }
    try {
      setSaving(true);
      const resp = await fetch(apiUrl('/api/warehouse/move-items'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ moveItems, targetObjectId, moveDate }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        if (data?.error === 'target_object_not_found') throw new Error('target_not_found');
        if (data?.error === 'move_date_invalid') throw new Error('date_invalid');
        if (data?.error === 'quantity_exceeds_available') throw new Error('qty_exceeds');
        throw new Error(data?.error || 'move_failed');
      }
      const movedCount = Number(data?.movedCount) || 0;
      setMoveModalOpen(false);
      setSelectedItemIds(new Set());
      await load();
      alert(`Перемещено позиций: ${movedCount}`);
    } catch (err) {
      if (String(err?.message || '') === 'target_not_found') {
        alert('Объект назначения не найден');
      } else if (String(err?.message || '') === 'date_invalid') {
        alert('Укажите корректную дату перемещения');
      } else if (String(err?.message || '') === 'qty_exceeds') {
        alert('Указано количество больше доступного для одной из позиций');
      } else {
        alert('Не удалось переместить выбранные позиции');
      }
    } finally {
      setSaving(false);
    }
  };

  const submitManual = async () => {
    const quantity = Number(manualForm.quantity);
    if (!manualForm.tmcId) {
      alert('Выберите позицию из справочника ТМЦ');
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      alert('Количество должно быть больше нуля');
      return;
    }
    try {
      setSaving(true);
      const resp = await fetch(apiUrl(`/api/warehouse/${encodeURIComponent(id)}/manual-items`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tmcId: manualForm.tmcId,
          quantity,
          comment: manualForm.comment,
        }),
      });
      if (!resp.ok) throw new Error('save_failed');
      setManualOpen(false);
      setManualForm({ tmcId: '', quantity: '1', comment: '', query: '' });
      await load();
    } catch {
      alert('Не удалось добавить позицию');
    } finally {
      setSaving(false);
    }
  };

  const submitWriteOff = async () => {
    if (!writeOffTarget) return;
    if (!writeOffDate) {
      alert('Укажите дату списания');
      return;
    }
    try {
      setSaving(true);
      const resp = await fetch(apiUrl(`/api/warehouse/items/${encodeURIComponent(writeOffTarget.id)}/writeoff`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ writeOffDate, writeOffComment }),
      });
      if (!resp.ok) throw new Error('writeoff_failed');
      setWriteOffTarget(null);
      setWriteOffDate(todayIso());
      setWriteOffComment('');
      await load();
    } catch {
      alert('Не удалось списать позицию');
    } finally {
      setSaving(false);
    }
  };

  const openBatchWriteOffModal = () => {
    if (!selectedCount) {
      alert('Отметьте галочками позиции для списания');
      return;
    }
    setBatchWriteOffDate(todayIso());
    setBatchWriteOffComment('');
    setBatchWriteOffOpen(true);
  };

  const submitBatchWriteOff = async () => {
    const itemIds = Array.from(selectedItemIds).map((value) => Number(value)).filter((value) => Number.isInteger(value) && value > 0);
    if (!itemIds.length) {
      alert('Нет выбранных позиций для списания');
      return;
    }
    if (!batchWriteOffDate) {
      alert('Укажите дату списания');
      return;
    }
    try {
      setSaving(true);
      const resp = await fetch(apiUrl('/api/warehouse/items/writeoff-batch'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itemIds,
          writeOffDate: batchWriteOffDate,
          writeOffComment: batchWriteOffComment,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        if (data?.error === 'writeoff_date_invalid') {
          alert('Укажите корректную дату списания');
          return;
        }
        throw new Error(data?.error || 'batch_writeoff_failed');
      }
      const count = Number(data?.writtenOffCount) || 0;
      setBatchWriteOffOpen(false);
      setSelectedItemIds(new Set());
      await load();
      alert(`Списано позиций: ${count}`);
    } catch {
      alert('Не удалось списать выбранные позиции');
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectAllVisible = (checked) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      visibleItemIds.forEach((idValue) => {
        if (checked) next.add(idValue);
        else next.delete(idValue);
      });
      return next;
    });
  };

  const submitCalcImport = async () => {
    if (!calcImportForm.objectCode) {
      alert('Выберите расчет');
      return;
    }
    try {
      setSaving(true);
      const resp = await fetch(apiUrl(`/api/warehouse/${encodeURIComponent(id)}/import-card-amortization`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ objectCode: Number(calcImportForm.objectCode) }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error || 'calc_import_failed');
      setCalcImportOpen(false);
      setCalcImportForm({ objectCode: '' });
      await load();
      const skippedText = Number(data?.skippedCount) > 0 ? ` Пропущено строк: ${data.skippedCount}.` : '';
      alert(`Из расчета добавлено позиций: ${Number(data?.addedCount) || 0}.${skippedText}`);
    } catch {
      alert('Не удалось добавить позиции из расчета');
    } finally {
      setSaving(false);
    }
  };

  const calcLabel = (item) => {
    if (!item) return '';
    const period = item.periodMonth && item.periodYear ? `${String(item.periodMonth).padStart(2, '0')}.${item.periodYear}` : 'без периода';
    const rows = Number(item.amortizationRows) || 0;
    return `Расчет №${item.objectCode} • ${period} • строк амортизации: ${rows}`;
  };

  const statCardStyle = {
    border: '1px solid #dbe1ea',
    borderRadius: 16,
    background: '#fff',
    padding: '14px 16px',
    boxShadow: '0 14px 28px rgba(15,23,42,0.05)',
  };
  const tableWrapStyle = {
    border: '1px solid #dbe1ea',
    borderRadius: 18,
    overflow: 'auto',
    background: '#fff',
    boxShadow: '0 14px 28px rgba(15,23,42,0.05)',
  };
  const thStyle = {
    padding: '10px 12px',
    textAlign: 'left',
    background: '#f8fafc',
    borderBottom: '1px solid #dbe1ea',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  };
  const tdStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #e8edf5',
    verticalAlign: 'top',
  };
  const actionButtonStyle = {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid #cbd5e1',
    background: '#fff',
    cursor: 'pointer',
  };

  return (
    <div style={{ padding: '24px 0 40px 0' }}>
      <style>{`
        @media print {
          .warehouse-no-print { display: none !important; }
          .warehouse-print-hide { display: none !important; }
          .warehouse-writtenoff-section { display: none !important; }
          body { background: #fff !important; }
        }
        @media print {
          body.warehouse-no-prices .warehouse-price-col { display: none !important; }
          body.warehouse-no-prices .warehouse-price-stat { display: none !important; }
        }
      `}</style>

      <div className="warehouse-no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Link to="/warehouse">&larr; К журналу склада</Link>
        <h2 style={{ margin: 0 }}>Склад объекта</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={load} style={actionButtonStyle}>Обновить</button>
          <button onClick={openMoveModal} disabled={saving || selectedCount === 0} style={{ ...actionButtonStyle, background: '#e0f2fe', borderColor: '#38bdf8' }}>
            Переместить выбранные позиции
          </button>
          <button onClick={openBatchWriteOffModal} disabled={saving || selectedCount === 0} style={{ ...actionButtonStyle, background: '#fef3c7', borderColor: '#f59e0b', color: '#92400e' }}>
            Списать выбранные
          </button>
          <button onClick={() => setCalcImportOpen(true)} disabled={saving} style={{ ...actionButtonStyle, background: '#ede9fe', borderColor: '#a78bfa' }}>Добавить из расчета</button>
          <button onClick={() => setManualOpen(true)} style={{ ...actionButtonStyle, background: '#e7f6d4', borderColor: '#8bc34a' }}>Добавить вручную</button>
          <button onClick={() => window.print()} style={actionButtonStyle}>Печать остатков</button>
          <button onClick={() => {
            document.body.classList.add('warehouse-no-prices');
            const cleanup = () => { document.body.classList.remove('warehouse-no-prices'); window.removeEventListener('afterprint', cleanup); };
            window.addEventListener('afterprint', cleanup);
            window.print();
          }} style={actionButtonStyle}>Печать без цен</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center' }}>Загрузка…</div>
      ) : error ? (
        <div style={{ textAlign: 'center', color: '#c00' }}>{error}</div>
      ) : !object ? (
        <div style={{ textAlign: 'center', color: '#c00' }}>Объект не найден</div>
      ) : (
        <>
          <div style={{ marginBottom: 18, padding: '18px 20px', borderRadius: 18, background: 'linear-gradient(135deg, #eff6ff, #ffffff)', border: '1px solid #dbe1ea' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>Объект</div>
                <div style={{ fontSize: 26, fontWeight: 800 }}>{object.name}</div>
                <div style={{ marginTop: 6, color: '#334155' }}>Код: <strong>{object.codeBase}</strong></div>
                <div style={{ marginTop: 4, color: '#334155' }}>Адрес: <strong>{object.address || '—'}</strong></div>
                <div style={{ marginTop: 4, color: '#334155' }}>Заказчик: <strong>{object.customer || '—'}</strong></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(180px, 1fr))', gap: 12, flex: '1 1 420px' }}>
                <div style={statCardStyle}>
                  <div style={{ fontSize: 13, color: '#64748b' }}>Позиции в остатке</div>
                  <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4 }}>{groupedActiveItems.length}</div>
                </div>
                <div style={statCardStyle}>
                  <div style={{ fontSize: 13, color: '#64748b' }}>Количество в остатке</div>
                  <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4 }}>{fmtQty(summary.activeQuantity)}</div>
                </div>
                <div className="warehouse-price-stat" style={statCardStyle}>
                  <div style={{ fontSize: 13, color: '#64748b' }}>Сумма остатка</div>
                  <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4 }}>{fmtMoney(summary.activeAmount)} ₽</div>
                </div>
                <div style={statCardStyle}>
                  <div style={{ fontSize: 13, color: '#64748b' }}>Списано позиций</div>
                  <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4 }}>{summary.writtenOffPositions}</div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>Остатки</h3>
            <div style={{ color: '#64748b', fontSize: 13, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span>Ручных позиций: <strong>{summary.manualPositions}</strong></span>
              <span>Выбрано: <strong>{selectedCount}</strong></span>
            </div>
          </div>

          <div style={tableWrapStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th className="warehouse-no-print" style={thStyle}>Выбор</th>
                  <th style={thStyle}>Наименование</th>
                  <th style={thStyle}>Кол-во</th>
                  <th style={thStyle}>Ед.</th>
                  <th className="warehouse-price-col" style={thStyle}>Средняя цена</th>
                  <th className="warehouse-price-col" style={thStyle}>Сумма</th>
                  <th className="warehouse-print-hide" style={thStyle}>Аморт., мес.</th>
                  <th className="warehouse-no-print" style={thStyle}>Действия</th>
                </tr>
                <tr className="warehouse-no-print">
                  <th style={{ ...thStyle, background: '#fdfdff' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                      />
                      <span style={{ fontSize: 12, color: '#475569' }}>Все</span>
                    </label>
                  </th>
                  <th style={{ ...thStyle, background: '#fdfdff' }}>
                    <input
                      value={positionFilters.name}
                      onChange={(e) => setPositionFilters((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Поиск позиции..."
                      style={filterInputStyle}
                    />
                  </th>
                  <th style={{ ...thStyle, background: '#fdfdff' }} />
                  <th style={{ ...thStyle, background: '#fdfdff' }}>
                    <input
                      value={positionFilters.unit}
                      onChange={(e) => setPositionFilters((prev) => ({ ...prev, unit: e.target.value }))}
                      placeholder="Ед."
                      style={filterInputStyle}
                    />
                  </th>
                  <th className="warehouse-price-col" style={{ ...thStyle, background: '#fdfdff' }} />
                  <th className="warehouse-price-col" style={{ ...thStyle, background: '#fdfdff' }} />
                  <th className="warehouse-print-hide" style={{ ...thStyle, background: '#fdfdff' }}>
                    <input
                      value={positionFilters.amortization}
                      onChange={(e) => setPositionFilters((prev) => ({ ...prev, amortization: e.target.value }))}
                      placeholder="Аморт."
                      style={filterInputStyle}
                    />
                  </th>
                  <th className="warehouse-no-print" style={{ ...thStyle, background: '#fdfdff' }} />
                </tr>
              </thead>
              <tbody>
                {filteredGroupedActiveItems.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>В остатках по объекту ничего нет</td>
                  </tr>
                ) : filteredGroupedActiveItems.map((group) => {
                  const expanded = expandedKeys.has(group.key);
                  const selectedInGroup = group.details.reduce((acc, item) => acc + (selectedItemIds.has(Number(item.id)) ? 1 : 0), 0);
                  const groupAllSelected = group.details.length > 0 && selectedInGroup === group.details.length;
                  return (
                    <React.Fragment key={group.key}>
                      <tr>
                        <td className="warehouse-no-print" style={tdStyle}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={groupAllSelected}
                              onChange={(e) => toggleGroupSelection(group, e.target.checked)}
                            />
                            <span style={{ fontSize: 12, color: '#64748b' }}>{selectedInGroup}/{group.details.length}</span>
                          </label>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <button className="warehouse-no-print" onClick={() => toggleExpanded(group.key)} style={{ ...actionButtonStyle, padding: '2px 8px', minWidth: 34 }}>
                              {expanded ? '−' : '+'}
                            </button>
                            <div style={{ fontWeight: 700, lineHeight: 1.15, wordBreak: 'break-word' }}>{group.name}</div>
                          </div>
                        </td>
                        <td style={tdStyle}>{fmtQty(group.quantity)}</td>
                        <td style={tdStyle}>{group.unit || '—'}</td>
                        <td className="warehouse-price-col" style={tdStyle}>{fmtMoney(group.avgPrice)}</td>
                        <td className="warehouse-price-col" style={tdStyle}>{fmtMoney(group.totalAmount)}</td>
                        <td className="warehouse-print-hide" style={tdStyle}>{group.amortizationPeriod || '—'}</td>
                        <td className="warehouse-no-print" style={tdStyle}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button onClick={() => toggleExpanded(group.key)} style={actionButtonStyle}>{expanded ? 'Скрыть' : 'Раскрыть'}</button>
                          </div>
                        </td>
                      </tr>
                      {expanded ? group.details.map((item) => (
                        <tr key={item.id} style={{ background: '#f8fafc' }}>
                          <td className="warehouse-no-print" style={tdStyle}>
                            <input
                              type="checkbox"
                              checked={selectedItemIds.has(Number(item.id))}
                              onChange={(e) => toggleItemSelection(item.id, e.target.checked)}
                            />
                          </td>
                          <td style={{ ...tdStyle, paddingLeft: 54 }}>
                            <div>{sourceLabel(item)}</div>
                            <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>Добавлено: {fmtDateTime(item.createdAt)}</div>
                            {item.movedAt ? (
                              <div style={{ marginTop: 4, fontSize: 12, color: '#0369a1' }}>
                                Перемещено: {fmtDate(item.movedAt)}{movedFromLabel(item) ? ` из ${movedFromLabel(item)}` : ''}
                              </div>
                            ) : null}
                            {item.manualComment ? <div style={{ marginTop: 4, color: '#64748b', fontSize: 12 }}>{item.manualComment}</div> : null}
                            {item.supplierLink ? <a href={item.supplierLink} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 4, fontSize: 12 }}>Ссылка поставщика</a> : null}
                          </td>
                          <td style={tdStyle}>{fmtQty(item.quantity)}</td>
                          <td style={tdStyle}>{item.unit || '—'}</td>
                          <td className="warehouse-price-col" style={tdStyle}>{fmtMoney(item.price)}</td>
                          <td className="warehouse-price-col" style={tdStyle}>{fmtMoney((Number(item.quantity) || 0) * (Number(item.price) || 0))}</td>
                          <td className="warehouse-print-hide" style={tdStyle}>{item.amortizationPeriod || '—'}</td>
                          <td className="warehouse-no-print" style={tdStyle}>
                            <button
                              onClick={() => {
                                setWriteOffTarget(item);
                                setWriteOffDate(todayIso());
                                setWriteOffComment('');
                              }}
                              style={{ ...actionButtonStyle, borderColor: '#f59e0b', color: '#92400e' }}
                            >
                              Списать
                            </button>
                          </td>
                        </tr>
                      )) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {writtenOffItems.length > 0 ? (
            <div className="warehouse-writtenoff-section" style={{ marginTop: 24 }}>
              <h3 style={{ margin: '0 0 10px 0' }}>Списанные позиции</h3>
              <div style={tableWrapStyle}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Наименование</th>
                      <th style={thStyle}>Источник</th>
                      <th style={thStyle}>Кол-во</th>
                      <th style={thStyle}>Дата списания</th>
                      <th style={thStyle}>Комментарий</th>
                    </tr>
                  </thead>
                  <tbody>
                    {writtenOffItems.map((item) => (
                      <tr key={item.id}>
                        <td style={tdStyle}>{item.name}</td>
                        <td style={tdStyle}>
                          <div>{sourceLabel(item)}</div>
                          {item.movedAt ? (
                            <div style={{ marginTop: 4, fontSize: 12, color: '#0369a1' }}>
                              Перемещено: {fmtDate(item.movedAt)}{movedFromLabel(item) ? ` из ${movedFromLabel(item)}` : ''}
                            </div>
                          ) : null}
                        </td>
                        <td style={tdStyle}>{fmtQty(item.quantity)}</td>
                        <td style={tdStyle}>{item.writeOffDate || '—'}</td>
                        <td style={tdStyle}>{item.writeOffComment || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}

      {manualOpen ? (
        <div className="warehouse-no-print" onClick={() => !saving && setManualOpen(false)} style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 640, maxWidth: '94%', background: '#fff', borderRadius: 14, padding: 20 }}>
            <h3 style={{ marginTop: 0 }}>Добавить позицию вручную</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, alignItems: 'center' }}>
              <label style={{ textAlign: 'right' }}>Поиск позиции:</label>
              <input
                value={manualForm.query}
                onChange={(e) => setManualForm((prev) => ({ ...prev, query: e.target.value }))}
                placeholder="Поиск по названию, коду, ед., ссылке"
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
              />
              <label style={{ textAlign: 'right' }}>Позиция ТМЦ:</label>
              <select
                value={manualForm.tmcId}
                onChange={(e) => setManualForm((prev) => ({ ...prev, tmcId: e.target.value }))}
                disabled={tmcLoading}
                size={10}
                style={{
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  maxHeight: 220,
                  overflowY: 'auto',
                }}
              >
                <option value="">{tmcLoading ? 'Загрузка...' : 'Выберите позицию'}</option>
                {filteredTmcOptions.map((item) => (
                  <option key={item.id} value={item.id}>{item.name} ({item.id})</option>
                ))}
              </select>
              <label style={{ textAlign: 'right' }}>Количество:</label>
              <input type="number" min="0.001" step="0.001" value={manualForm.quantity} onChange={(e) => setManualForm((prev) => ({ ...prev, quantity: e.target.value }))} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }} />
              <label style={{ textAlign: 'right' }}>Единица:</label>
              <input value={selectedTmc?.unit || ''} readOnly style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc' }} />
              <label style={{ textAlign: 'right' }}>Цена, ₽:</label>
              <input value={selectedTmc ? fmtMoney(selectedTmc.price) : ''} readOnly style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc' }} />
              <label style={{ textAlign: 'right' }}>Амортизация, мес.:</label>
              <input value={selectedTmc?.amortization_period || ''} readOnly style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc' }} />
              <label style={{ textAlign: 'right' }}>Ссылка поставщика:</label>
              <input value={selectedTmc?.supplier_link || ''} readOnly style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc' }} />
              <label style={{ textAlign: 'right' }}>Комментарий:</label>
              <textarea value={manualForm.comment} onChange={(e) => setManualForm((prev) => ({ ...prev, comment: e.target.value }))} rows={3} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button onClick={() => setManualOpen(false)} disabled={saving} style={actionButtonStyle}>Отмена</button>
              <button onClick={submitManual} disabled={saving} style={{ ...actionButtonStyle, background: '#e7f6d4', borderColor: '#8bc34a' }}>Сохранить</button>
            </div>
          </div>
        </div>
      ) : null}

      {calcImportOpen ? (
        <div className="warehouse-no-print" onClick={() => !saving && setCalcImportOpen(false)} style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 680, maxWidth: '94%', background: '#fff', borderRadius: 14, padding: 20 }}>
            <h3 style={{ marginTop: 0 }}>Добавить из расчета</h3>
            <div style={{ color: '#64748b', marginBottom: 14 }}>Будут добавлены все строки со страницы Амортизация выбранного расчета.</div>
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, alignItems: 'center' }}>
              <label style={{ textAlign: 'right' }}>Расчет:</label>
              <select value={calcImportForm.objectCode} onChange={(e) => setCalcImportForm({ objectCode: e.target.value })} disabled={calcLoading || saving} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                <option value="">{calcLoading ? 'Загрузка...' : 'Выберите расчет'}</option>
                {calcOptions.map((item) => (
                  <option key={item.objectCode} value={item.objectCode}>{calcLabel(item)}</option>
                ))}
              </select>
              <label style={{ textAlign: 'right' }}>Период:</label>
              <input value={selectedCalc ? (selectedCalc.periodMonth && selectedCalc.periodYear ? `${String(selectedCalc.periodMonth).padStart(2, '0')}.${selectedCalc.periodYear}` : '—') : ''} readOnly style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc' }} />
              <label style={{ textAlign: 'right' }}>Строк амортизации:</label>
              <input value={selectedCalc ? String(selectedCalc.amortizationRows || 0) : ''} readOnly style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc' }} />
              <label style={{ textAlign: 'right' }}>Статус:</label>
              <input value={selectedCalc?.status || ''} readOnly style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc' }} />
            </div>
            {!calcLoading && calcOptions.length === 0 ? (
              <div style={{ marginTop: 14, color: '#b91c1c' }}>Для этого объекта не найдено расчетов с заполненной страницей Амортизация.</div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button onClick={() => setCalcImportOpen(false)} disabled={saving} style={actionButtonStyle}>Отмена</button>
              <button onClick={submitCalcImport} disabled={saving || calcLoading || !calcImportForm.objectCode} style={{ ...actionButtonStyle, background: '#ede9fe', borderColor: '#a78bfa' }}>Добавить страницу Амортизация</button>
            </div>
          </div>
        </div>
      ) : null}

      {writeOffTarget ? (
        <div className="warehouse-no-print" onClick={() => !saving && setWriteOffTarget(null)} style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 520, maxWidth: '94%', background: '#fff', borderRadius: 14, padding: 20 }}>
            <h3 style={{ marginTop: 0 }}>Списать позицию</h3>
            <div style={{ marginBottom: 12, color: '#334155' }}><strong>{writeOffTarget.name}</strong></div>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, alignItems: 'center' }}>
              <label style={{ textAlign: 'right' }}>Дата списания:</label>
              <input type="date" value={writeOffDate} onChange={(e) => setWriteOffDate(e.target.value)} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }} />
              <label style={{ textAlign: 'right' }}>Комментарий:</label>
              <textarea value={writeOffComment} onChange={(e) => setWriteOffComment(e.target.value)} rows={3} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button onClick={() => setWriteOffTarget(null)} disabled={saving} style={actionButtonStyle}>Отмена</button>
              <button onClick={submitWriteOff} disabled={saving} style={{ ...actionButtonStyle, background: '#fef3c7', borderColor: '#f59e0b' }}>Списать</button>
            </div>
          </div>
        </div>
      ) : null}

      {batchWriteOffOpen ? (
        <div className="warehouse-no-print" onClick={() => !saving && setBatchWriteOffOpen(false)} style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: '94%', background: '#fff', borderRadius: 14, padding: 20 }}>
            <h3 style={{ marginTop: 0 }}>Списать выбранные позиции</h3>
            <div style={{ marginBottom: 12, color: '#334155' }}>Выбрано позиций: <strong>{selectedCount}</strong></div>
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, alignItems: 'center' }}>
              <label style={{ textAlign: 'right' }}>Дата списания:</label>
              <input
                type="date"
                value={batchWriteOffDate}
                onChange={(e) => setBatchWriteOffDate(e.target.value)}
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
              />
              <label style={{ textAlign: 'right' }}>Комментарий:</label>
              <textarea
                value={batchWriteOffComment}
                onChange={(e) => setBatchWriteOffComment(e.target.value)}
                rows={3}
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button onClick={() => setBatchWriteOffOpen(false)} disabled={saving} style={actionButtonStyle}>Отмена</button>
              <button onClick={submitBatchWriteOff} disabled={saving || selectedCount === 0} style={{ ...actionButtonStyle, background: '#fef3c7', borderColor: '#f59e0b', color: '#92400e' }}>
                Списать выбранные
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {moveModalOpen ? (
        <div className="warehouse-no-print" onClick={() => !saving && setMoveModalOpen(false)} style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 760, maxWidth: '94%', background: '#fff', borderRadius: 14, padding: 20 }}>
            <h3 style={{ marginTop: 0 }}>Перемещение выбранных позиций</h3>
            <div style={{ color: '#334155', marginBottom: 12 }}>
              Выбрано позиций: <strong>{selectedCount}</strong>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 10, alignItems: 'center' }}>
              <label style={{ textAlign: 'right' }}>Поиск объекта:</label>
              <input
                value={moveForm.query}
                onChange={(e) => setMoveForm((prev) => ({ ...prev, query: e.target.value }))}
                placeholder="Код, название, адрес, заказчик"
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
              />

              <label style={{ textAlign: 'right' }}>Объект назначения:</label>
              <select
                size={8}
                value={moveForm.targetObjectId}
                onChange={(e) => setMoveForm((prev) => ({ ...prev, targetObjectId: e.target.value }))}
                disabled={objectsLoading}
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
              >
                {targetObjects.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.codeBase} • {row.name}{row.address ? ` • ${row.address}` : ''}
                  </option>
                ))}
              </select>

              <label style={{ textAlign: 'right' }}>Дата перемещения:</label>
              <input
                type="date"
                value={moveForm.moveDate}
                onChange={(e) => setMoveForm((prev) => ({ ...prev, moveDate: e.target.value }))}
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
              />
            </div>
            <div style={{ marginTop: 14, border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'auto', maxHeight: 260 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, position: 'sticky', top: 0 }}>Позиция</th>
                    <th style={{ ...thStyle, position: 'sticky', top: 0, width: 130 }}>Доступно</th>
                    <th style={{ ...thStyle, position: 'sticky', top: 0, width: 170 }}>Переместить</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItemsForMove.map((item) => (
                    <tr key={item.id}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600 }}>{item.name}</div>
                        <div style={{ marginTop: 3, fontSize: 12, color: '#64748b' }}>{sourceLabel(item)}</div>
                      </td>
                      <td style={tdStyle}>{fmtQty(item.quantity)} {item.unit || ''}</td>
                      <td style={tdStyle}>
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          value={moveQuantities[String(item.id)] ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setMoveQuantities((prev) => ({ ...prev, [String(item.id)]: val === '' ? '' : Number(val) }));
                          }}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!objectsLoading && targetObjects.length === 0 ? (
              <div style={{ marginTop: 10, color: '#b91c1c' }}>Подходящие объекты не найдены.</div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button onClick={() => setMoveModalOpen(false)} disabled={saving} style={actionButtonStyle}>Отмена</button>
              <button
                onClick={submitMoveSelected}
                disabled={saving || objectsLoading || !moveForm.targetObjectId || selectedCount === 0}
                style={{ ...actionButtonStyle, background: '#e0f2fe', borderColor: '#38bdf8' }}
              >
                Переместить
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}